import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { API_BASE } from '../config';
import { DEMO_DISABLED_TITLE } from '../utils/demoBanner';
import { scrollToTop } from '../utils/scrollToTop';
import { useConfirm } from '../components/ui/Modal';
import { useTranslation } from '../translations';
import { IconButton, slideIn, panelTopHairline } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { maPremium, superAdminFirmyBezPremium } from '../utils/planFirmy';
import { OpisBleduSerwera, bladZUstawien } from '../components/email/BladSerweraPoczty';
import PremiumNotice from '../components/premium/PremiumNotice';
import { usePremiumBlad } from '../components/premium/PremiumUpgrade';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// SVG Icons
const MailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);

const TestIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

// Styled Components
const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  min-height: calc(100vh - 48px);
  /* Reszta panelu (PanelContainer, ui/common.js) dostaje przy wejsciu na strone
     delikatny fade+wjazd z dolu. Ta strona miala WLASNY Container bez tej animacji. */
  animation: ${slideIn} 0.6s ease-out;

  ${panelTopHairline}
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
  padding-top: 8px;
`;

const HeaderCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const TitleIcon = styled.div`
  color: var(--primary);
  display: flex;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--text-muted);
`;

const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
`;

const CardTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardSubtitle = styled.p`
  margin: 0 0 24px 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.mb || '0'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: var(--text);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const HelpText = styled.p`
  margin: 6px 0 0 0;
  font-size: 12px;
  color: var(--text-muted);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--primary);
  color: white;
  box-shadow: var(--shadow-primary);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

const SuccessButton = styled(Button)`
  background: var(--success);
  color: var(--primary-contrast);
  box-shadow: var(--shadow-sm);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
`;

const DangerButton = styled(Button)`
  background: var(--danger);
  color: var(--primary-contrast);
  box-shadow: var(--shadow-sm);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
`;

const Alert = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;

  ${props => props.type === 'success' && `
    background: var(--success-bg);
    border: 1px solid var(--success-border);
    color: var(--success);
  `}

  ${props => props.type === 'error' && `
    background: var(--danger-bg);
    border: 1px solid var(--danger-border);
    color: var(--danger);
  `}

  ${props => props.type === 'info' && `
    background: var(--info-bg);
    border: 1px solid var(--info-border);
    color: var(--info);
  `}
`;

// Pola firmy bez Premium: `disabled` na fieldsecie wyłącza wszystkie kontrolki w środku naraz.
const PlanLock = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;

  &:disabled {
    opacity: 0.6;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;

  ${props => props.verified ? `
    background: var(--success-bg);
    color: var(--success);
  ` : `
    background: var(--warning-bg);
    color: var(--warning);
  `}
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--input-border);
  margin: 24px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleLabelMain = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: var(--text);
`;

const ToggleLabelSub = styled.span`
  font-size: 12px;
  color: var(--text-muted);
`;

const Toggle = styled.label`
  position: relative;
  width: 48px;
  height: 26px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    background: var(--input-border);
    border-radius: 34px;
    transition: 0.3s;

    &::before {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      left: 3px;
      bottom: 3px;
      background: white;
      border-radius: 50%;
      transition: 0.3s;
    }
  }

  input:checked + span {
    background: var(--primary);
  }

  input:checked + span::before {
    transform: translateX(22px);
  }
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;

const PresetButton = styled.button`
  padding: 10px 12px;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  overflow: hidden;

  &:hover {
    border-color: var(--primary);
    background: var(--table-row-hover);
  }

  small {
    display: block;
    font-weight: 400;
    color: var(--text-muted);
    font-size: 11px;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// SMTP Presets
const SMTP_PRESETS = [
  { name: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  { name: 'Outlook / O365', host: 'smtp.office365.com', port: 587, secure: false },
  { name: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 587, secure: false },
  { name: 'Zoho', host: 'smtp.zoho.eu', port: 587, secure: false },
  { name: 'OVH', host: 'ssl0.ovh.net', port: 465, secure: true },
  { name: 'nazwa.pl', host: 'mail.nazwa.pl', port: 465, secure: true },
];

function EmailSettings() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const isDemo = localStorage.getItem('isDemo') === '1';
  // F.7: własny serwer poczty jest w Premium. Serwer odrzuca zapis i test (403), a przy wysyłce
  // pomija zapisany SMTP firmy na Basic; usunięcie konfiguracji zostaje dostępne.
  // `planFirmy` z GET /api/email-settings: aktualny plan, także w karcie otwartej przed jego zmianą.
  const [planFirmy, setPlanFirmy] = useState(null);
  const bezPremium = !maPremium(planFirmy);
  const superAdminBezPremium = superAdminFirmyBezPremium(planFirmy);
  const tytulBlokady = isDemo ? DEMO_DISABLED_TITLE : (bezPremium ? t('emailSettings.premiumTitle') : undefined);
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_pass: '',
    from_name: '',
    from_email: '',
    reply_to: '',
    bcc_email: '',
    is_active: true,
  });

  const [isVerified, setIsVerified] = useState(false);
  // Ostatni błąd serwera poczty przy wysyłce `{ rodzaj, czas }` (migracja 065) albo null.
  const [bladWysylki, setBladWysylki] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState(null); // { type: 'success'|'error'|'info', text: '' }
  const pokazBladPremium = usePremiumBlad();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/email-settings`, { credentials: 'include' });
      const data = await res.json();
      if (typeof data.organizationPlan === 'string') setPlanFirmy(data.organizationPlan);

      if (data.settings) {
        setSettings({
          smtp_host: data.settings.smtp_host || '',
          smtp_port: data.settings.smtp_port || 587,
          smtp_secure: data.settings.smtp_secure || false,
          smtp_user: data.settings.smtp_user || '',
          smtp_pass: '', // nigdy nie zwracamy hasła
          from_name: data.settings.from_name || '',
          from_email: data.settings.from_email || '',
          reply_to: data.settings.reply_to || '',
          bcc_email: data.settings.bcc_email || '',
          is_active: data.settings.is_active !== false,
        });
        setIsVerified(data.settings.is_verified || false);
        setBladWysylki(bladZUstawien(data.settings));
        setHasExisting(true);
      }
    } catch (err) {
      console.error('Error loading email settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/email-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setMessage({ type: 'success', text: t('emailSettings.saved') });
        setHasExisting(true);
        setIsVerified(false); // trzeba przetestować po zmianie
        setBladWysylki(null); // zapis zdejmuje ostatni błąd wysyłki (serwer robi to samo)
      } else if (!pokazBladPremium(data)) {
        setMessage({ type: 'error', text: data.error || t('emailSettings.saveError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('emailSettings.connectionError') });
    } finally {
      setSaving(false);
      scrollToTop();
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);

    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/email-settings/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify({ ...settings, test_email: testEmail || '' }),
      });

      const data = await res.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        setIsVerified(true);
        // Serwer zdejmuje ostatni błąd wysyłki tylko po udanym teście ZAPISANYCH ustawień
        // (bez nowego hasła, ten sam serwer, SSL i nadawca) i mówi to polem sendErrorCleared;
        // test innych wartości z formularza zostawia baner, bo zapisane ustawienia dalej zawodzą.
        if (data.sendErrorCleared) setBladWysylki(null);
      } else if (!pokazBladPremium(data)) {
        setMessage({ type: 'error', text: data.error || t('emailSettings.testFailed') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('emailSettings.connectionError') });
    } finally {
      setTesting(false);
      scrollToTop();
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('emailSettings.deleteConfirmTitle'),
      message: t('emailSettings.deleteConfirmBody'),
      tone: 'danger',
      confirmLabel: t('common.delete'),
    });
    if (!ok) return;

    setDeleting(true);
    setMessage(null);

    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/email-settings`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf },
        credentials: 'include',
      });

      const data = await res.json();

      if (data.success) {
        setSettings({
          smtp_host: '', smtp_port: 587, smtp_secure: false,
          smtp_user: '', smtp_pass: '',
          from_name: '', from_email: '', reply_to: '', bcc_email: '',
          is_active: true,
        });
        setHasExisting(false);
        setIsVerified(false);
        setMessage({ type: 'info', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('emailSettings.connectionError') });
    } finally {
      setDeleting(false);
      scrollToTop();
    }
  };

  const applyPreset = (preset) => {
    setSettings(prev => ({
      ...prev,
      smtp_host: preset.host,
      smtp_port: preset.port,
      smtp_secure: preset.secure,
    }));
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <HeaderCenter>
            <Title><TitleIcon><MailIcon /></TitleIcon> {t('emailSettings.title')}</Title>
            <Subtitle>{t('common.loading')}</Subtitle>
          </HeaderCenter>
        </Header>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <HeaderCenter>
          <Title><TitleIcon><MailIcon /></TitleIcon> {t('emailSettings.title')}</Title>
          <Subtitle>{t('emailSettings.subtitle')}</Subtitle>
        </HeaderCenter>
      </Header>
      <TylkoOdczytWPrzerwie>

      {/* Info — na samej górze, żeby instrukcję dało się przeczytać PRZED wypełnianiem pól */}
      <Card>
        <CardTitle><InfoIcon /> {t('emailSettings.helpTitle')}</CardTitle>
        <CardSubtitle style={{ marginBottom: 0 }}>
          {t('emailSettings.helpBody')}
        </CardSubtitle>

        <div style={{ marginTop: '20px', background: 'var(--table-row-hover)', borderRadius: '12px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700 }}>{t('emailSettings.gmailTitle')}</h4>
          <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '2', fontSize: '14px', color: 'var(--text-muted)' }}>
            <li>{t('emailSettings.gmailStep1')}</li>
            <li>{t('emailSettings.gmailStep2')} <strong>myaccount.google.com/security</strong></li>
            <li>{t('emailSettings.gmailStep3a')} <strong>{t('emailSettings.gmailStep3b')}</strong> {t('emailSettings.gmailStep3c')}</li>
            <li>{t('emailSettings.gmailStep4')} <strong>myaccount.google.com/apppasswords</strong></li>
            <li>{t('emailSettings.gmailStep5a')} <strong>the.rENT</strong> {t('emailSettings.gmailStep5b')} <strong>{t('emailSettings.gmailStep5c')}</strong></li>
            <li>{t('emailSettings.gmailStep6a')} <strong>{t('emailSettings.gmailStep6b')}</strong> {t('emailSettings.gmailStep6c')}</li>
            <li>{t('emailSettings.gmailStep7')}</li>
          </ol>

          <div style={{ margin: '12px 0 0 20px', fontSize: '13px', lineHeight: '1.8' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '500px' }}>
              <tbody>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblHost')}</td><td style={{ color: 'var(--text-muted)' }}>smtp.gmail.com</td></tr>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblPort')}</td><td style={{ color: 'var(--text-muted)' }}>{t('emailSettings.tblPortValue')}</td></tr>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblSsl')}</td><td style={{ color: 'var(--text-muted)' }}>{t('emailSettings.tblSslValue')}</td></tr>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblLogin')}</td><td style={{ color: 'var(--text-muted)' }}>{t('emailSettings.tblLoginValue')}</td></tr>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblPassword')}</td><td style={{ color: 'var(--text-muted)' }}>{t('emailSettings.tblPasswordValue')}</td></tr>
                <tr><td style={{ paddingRight: '16px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>{t('emailSettings.tblFrom')}</td><td style={{ color: 'var(--text-muted)' }}>{t('emailSettings.tblFromValue')}</td></tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '16px', padding: '12px 16px', background: 'var(--card-bg)', borderLeft: '4px solid var(--warning)', borderRadius: '4px', fontSize: '13px', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text)' }}>{t('emailSettings.importantLabel')}</strong> {t('emailSettings.gmailWarning')}
          </div>
        </div>

        <div style={{ marginTop: '16px', background: 'var(--table-row-hover)', borderRadius: '12px', padding: '20px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: 700 }}>{t('emailSettings.otherServersTitle')}</h4>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
            {t('emailSettings.otherServersBody')}
          </p>
        </div>
      </Card>

      {isDemo && (
        <Alert type="info">
          <InfoIcon /> {t('emailSettings.demoNotice')}
        </Alert>
      )}

      {bezPremium && !isDemo && (
        <PremiumNotice
          feature="own_smtp"
          title={t('emailSettings.premiumTitle')}
          style={{ marginBottom: 20 }}
          data-cy="email-settings-premium"
        >
          {t('emailSettings.premiumBody')}
          {hasExisting && <> {t('emailSettings.premiumSavedNote')}</>}
        </PremiumNotice>
      )}

      {superAdminBezPremium && !isDemo && (
        <Alert type="info" data-cy="email-settings-superadmin-plan">
          <InfoIcon />
          <span>{t('emailSettings.superAdminNotPremium')}</span>
        </Alert>
      )}

      {message && (
        <Alert type={message.type}>
          {message.type === 'success' ? <CheckIcon /> : message.type === 'info' ? <InfoIcon /> : null}
          {message.text}{message.type === 'error' ? '!' : ''}
        </Alert>
      )}

      {/* Ostatni błąd serwera poczty przy wysyłce (migracja 065). Tylko, gdy firma jest na Premium:
          na Basic jej serwer nie jest używany (lib/transportFirmy.js), więc stary błąd nic nie znaczy. */}
      {bladWysylki && planFirmy === 'premium' && !isDemo && (
        <Alert type="error" role="alert" data-cy="email-settings-send-error">
          <span>
            <strong>{t('emailSettings.sendErrorTitle')}</strong>{' '}
            <OpisBleduSerwera blad={bladWysylki} />
          </span>
        </Alert>
      )}

      {/* Status */}
      {hasExisting && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <CardTitle>{t('emailSettings.statusTitle')}</CardTitle>
              <CardSubtitle style={{ marginBottom: 0 }}>
                {t('emailSettings.senderLabel')} <strong>{settings.from_name}</strong> &lt;{settings.from_email}&gt;
              </CardSubtitle>
            </div>
            <StatusBadge verified={isVerified}>
              {isVerified ? <><CheckIcon /> {t('emailSettings.verified')}</> : <><InfoIcon /> {t('emailSettings.unverified')}</>}
            </StatusBadge>
          </div>
        </Card>
      )}

      <PlanLock disabled={bezPremium} data-cy="email-settings-plan-lock">
      {/* Serwer SMTP */}
      <Card>
        <CardTitle>{t('emailSettings.smtpTitle')}</CardTitle>
        <CardSubtitle>{t('emailSettings.smtpSubtitle')}</CardSubtitle>

        <PresetGrid>
          {SMTP_PRESETS.map(preset => (
            <PresetButton key={preset.name} onClick={() => applyPreset(preset)} type="button">
              {preset.name}
              <small>{preset.host}:{preset.port}</small>
            </PresetButton>
          ))}
        </PresetGrid>

        <FormGrid>
          <FormGroup>
            <Label>{t('emailSettings.hostLabel')}</Label>
            <Input
              type="text"
              value={settings.smtp_host}
              onChange={e => updateField('smtp_host', e.target.value)}
              placeholder={t('emailSettings.hostPlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>Port</Label>
            <SimpleSelect
              style={{ width: '100%' }}
              disabled={bezPremium}
              value={settings.smtp_port}
              onChange={val => updateField('smtp_port', Number(val))}
              options={[
                { value: 587, label: t('emailSettings.port587') },
                { value: 465, label: '465 (SSL)' },
                { value: 25, label: t('emailSettings.port25') },
                { value: 2525, label: t('emailSettings.port2525') },
              ]}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('emailSettings.loginLabel')}</Label>
            <Input
              type="text"
              value={settings.smtp_user}
              onChange={e => updateField('smtp_user', e.target.value)}
              placeholder={t('emailSettings.emailExamplePlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('emailSettings.passwordLabel')}</Label>
            <Input
              type="password"
              value={settings.smtp_pass}
              onChange={e => updateField('smtp_pass', e.target.value)}
              placeholder={hasExisting ? t('emailSettings.passwordKeep') : t('emailSettings.passwordEnter')}
            />
            {hasExisting && (
              <HelpText>{t('emailSettings.passwordHelp')}</HelpText>
            )}
          </FormGroup>
        </FormGrid>

        <ToggleRow>
          <ToggleLabel>
            <ToggleLabelMain>{t('emailSettings.sslLabel')}</ToggleLabelMain>
            <ToggleLabelSub>{t('emailSettings.sslHelp')}</ToggleLabelSub>
          </ToggleLabel>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.smtp_secure}
              onChange={e => updateField('smtp_secure', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </ToggleRow>
      </Card>

      {/* Dane nadawcy */}
      <Card>
        <CardTitle>{t('emailSettings.senderTitle')}</CardTitle>
        <CardSubtitle>{t('emailSettings.senderSubtitle')}</CardSubtitle>

        <FormGrid>
          <FormGroup>
            <Label>{t('emailSettings.fromNameLabel')}</Label>
            <Input
              type="text"
              value={settings.from_name}
              onChange={e => updateField('from_name', e.target.value)}
              placeholder={t('emailSettings.fromNamePlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('emailSettings.fromEmailLabel')}</Label>
            <Input
              type="email"
              value={settings.from_email}
              onChange={e => updateField('from_email', e.target.value)}
              placeholder={t('emailSettings.emailExamplePlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('emailSettings.replyToLabel')}</Label>
            <Input
              type="email"
              value={settings.reply_to}
              onChange={e => updateField('reply_to', e.target.value)}
              placeholder={t('emailSettings.replyToPlaceholder')}
            />
            <HelpText>{t('emailSettings.replyToHelp')}</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>{t('emailSettings.bccLabel')}</Label>
            <Input
              type="email"
              value={settings.bcc_email}
              onChange={e => updateField('bcc_email', e.target.value)}
              placeholder={t('emailSettings.bccPlaceholder')}
            />
            <HelpText>{t('emailSettings.bccHelp')}</HelpText>
          </FormGroup>
        </FormGrid>

        <Divider />

        <ToggleRow>
          <ToggleLabel>
            <ToggleLabelMain>{t('emailSettings.enableLabel')}</ToggleLabelMain>
            <ToggleLabelSub>{t('emailSettings.enableHelp')}</ToggleLabelSub>
          </ToggleLabel>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.is_active}
              onChange={e => updateField('is_active', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </ToggleRow>
      </Card>
      </PlanLock>

      {/* Przyciski */}
      <ButtonRow style={{ justifyContent: 'flex-end' }}>
        <PrimaryButton
          onClick={handleSave}
          disabled={saving || isDemo || bezPremium}
          title={tytulBlokady}
          data-cy="email-settings-save"
        >
          <SaveIcon />
          {saving ? t('common.saving') : t('emailSettings.save')}
        </PrimaryButton>
      </ButtonRow>

      {/* Test email */}
      <Card style={{ marginTop: '24px' }}>
        <CardTitle><TestIcon /> {t('emailSettings.testTitle')}</CardTitle>
        <CardSubtitle>{t('emailSettings.testSubtitle')}</CardSubtitle>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormGroup style={{ flex: 1, minWidth: '240px' }}>
            <Label>{t('emailSettings.testRecipientLabel')}</Label>
            <Input
              type="email"
              disabled={bezPremium}
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder={t('emailSettings.testRecipientPlaceholder')}
            />
          </FormGroup>
          <SuccessButton
            onClick={handleTest}
            disabled={testing || !settings.smtp_host || !settings.smtp_user || !testEmail || isDemo || bezPremium}
            title={tytulBlokady}
            style={{ marginBottom: '0', height: '42px' }}
          >
            <TestIcon />
            {testing ? t('emailSettings.testing') : t('emailSettings.testSend')}
          </SuccessButton>
        </div>
      </Card>

      <ButtonRow>

        {hasExisting && (
          <DangerButton
            onClick={handleDelete}
            disabled={deleting || isDemo}
            title={isDemo ? DEMO_DISABLED_TITLE : undefined}
          >
            <TrashIcon />
            {deleting ? t('emailSettings.deleting') : t('emailSettings.deleteConfig')}
          </DangerButton>
        )}
      </ButtonRow>

      </TylkoOdczytWPrzerwie>
    </Container>
  );
}

export default EmailSettings;
